<template>
    <div id="login_main">
        <div id="login_container" :class="{ 'system-use': $store.systemUse && !accepted }">
            <div class="space-less"></div>

            <div v-if="$store.showLoadingOverlay">
                <div id="loading_spinner"></div>
                <div id="loading_message">{{ $t('Authenticating') }}</div>
            </div>

            <SystemUse v-else-if="$store.systemUse && !accepted" @click="() => (accepted = true)" />

            <ChangePassword v-else-if="changePassword" @change="login" />

            <RsaChallenge v-else-if="challenge" :value="password" @change="login" @input="(val) => (password = val)" />

            <TwoFactor
                v-else-if="twoFactor"
                :value="password"
                :supported-types="twoFactorSupports"
                :text="twoFactor"
                @resend="resend"
                @change="login"
                @input="(val) => (password = val)"
            />

            <div v-else>
                <div id="login_welcome">
                    <div id="login_icon"></div>
                    <!-- eslint-disable vue/no-v-html -->
                    <div id="login_welcome_message" v-html="$t('Welcome to [x]').replace('[x]', $t('PRODUCT'))"></div>
                    <div id="login_login_message">{{ $t('Log in to get going') }}</div>
                </div>
                <div id="login_content">
                    <div v-if="error" id="login_error_container">
                        <div id="login_error_icon"></div>
                        <div id="login_error">{{ error }}</div>
                    </div>

                    <ul id="login_credentials" :class="{ 'no-margin': !!error }">
                        <li>
                            <label class="login-label">{{ $t('Username') }}</label>
                            <cic-textbox
                                id="username"
                                :value="username"
                                input-id="username-input"
                                @keyup.enter="login"
                                @input="(val) => (username = val)"
                            />
                            <label class="login-explain">{{ $t('Enter your username') }}</label>
                        </li>
                        <li>
                            <label class="login-label">{{ $t('Organisation') }}</label>
                            <cic-textbox
                                id="organisation"
                                :value="organisation"
                                @keyup.enter="login"
                                @input="(val) => (organisation = val)"
                            />
                            <label class="login-explain">{{ $t('Enter your organisation') }}</label>
                        </li>
                        <li>
                            <label class="login-label">{{ $t('Password') }}</label>
                            <cic-textbox
                                id="password"
                                :value="password"
                                type="password"
                                @keyup.enter="login"
                                @input="(val) => (password = val)"
                            />
                            <label class="login-explain">{{ $t('Enter your password') }}</label>
                        </li>
                    </ul>

                    <div class="cic-control-bar">
                        <cic-button class="ok" @click="login">
                            {{ $t('Login') }}
                        </cic-button>
                    </div>
                </div>
            </div>

            <div class="space-more"></div>
        </div>
    </div>
</template>

<script>
    import CicButton from '@/components/CicButton';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import { post, redirect } from '@/utils/Http';
    import ChangePassword from './ChangePassword';
    import RsaChallenge from './RsaChallenge';
    import SystemUse from './SystemUse';
    import TwoFactor from './TwoFactor';
    import './resources/styles/login.scss';

    export default {
        name: 'PortalLogin',
        components: {
            CicButton,
            CicTextbox,
            ChangePassword,
            RsaChallenge,
            SystemUse,
            TwoFactor,
        },
        data() {
            return {
                accepted: false,
                authState: '',
                challenge: false,
                changePassword: false,
                error: null,
                orgId: null,
                organisation: null,
                password: null,
                show: true,
                twoFactor: null,
                twoFactorSupports: [],
                userId: null,
                username: null,
            };
        },
        created() {
            if (!this.error && this.$route.query.back) {
                this.error = this.$t('You have been logged out of portal');
            }
        },
        methods: {
            login(newPassword = null, newPassword2 = null) {
                this.$store.setShowLoadingOverlay();
                let params = {
                    username: this.username,
                    organisation: this.organisation,
                    password: this.password,
                    newpassword: newPassword,
                    newpassword2: newPassword2,
                    challenge: this.challenge ? '1' : '0',
                    authstate: this.authState,
                };
                if (this.twoFactor) {
                    params = {
                        userId: this.userId,
                        orgId: this.orgId,
                        token: this.password,
                        '2fachallenge': '1',
                    };
                }

                this.error = null;
                this.show = false;
                post('login/rtsinfonia.json', params).then((response) => {
                    this.show = true;
                    this.challenge = false;
                    this.changePassword = false;
                    this.twoFactor = false;
                    this.authState = '';
                    this.$store.hideLoadingOverlay();

                    if (response.status !== 'passwordexpired') {
                        this.password = '';
                    }

                    if (response.status === 'OK') {
                        this.$store.setRequestParams({
                            ...this.$store.requestParams,
                            securityToken: response.user.session.securityToken,
                        });
                        if (response.action === 'login/tree') {
                            this.$store.setUser(response.user);
                            this.$router.push('/login/tree');
                        } else {
                            redirect('menu?lang=' + this.$store.requestParams.lang);
                        }
                    } else if (response.status === 'passwordexpired') {
                        this.error = this.$t('Your password has expired and must be changed.');
                        this.changePassword = true;
                    } else if (response.status === 'challenge') {
                        this.challenge = true;
                        this.authState = response.authstate;
                    } else if (response.status === '2fachallenge') {
                        this.twoFactor = this.$t(response.type === 1 ? '2FA_HELP_SMS' : '2FA_HELP_EMAIL').replace(
                            /\[x]/g,
                            response.destination,
                        );
                        this.twoFactorSupports = response.supports;
                        this.orgId = response.orgId;
                        this.$store.setThemeName(response.theme);
                        this.userId = response.userId;
                    } else if (response.status === 'usepassword') {
                        this.username = '';
                        this.organisation = '';
                        this.error = this.$t(
                            'As a security check, please log in again using your RTSinfonia password.',
                        );
                    } else if (response.status === 'usesso') {
                        this.username = '';
                        this.organisation = '';
                        this.error = this.$t('Use single sign-on to login');
                    } else {
                        this.username = '';
                        this.organisation = '';
                        if (response.status === 'fail') {
                            this.error = this.$t('Please check you have entered your details correctly');
                        }
                    }
                });
            },
            resend(type) {
                this.error = null;
                this.show = false;
                post('login/resend2fa', { userId: this.userId, orgId: this.orgId, type: type }).then((response) => {
                    this.show = true;
                    if (response.status === 'OK') {
                        this.twoFactor = this.$t(type === 1 ? '2FA_HELP_SMS' : '2FA_HELP_EMAIL').replace(
                            /\[x]/g,
                            response.destination,
                        );
                    } else {
                        this.error = this.$t(response.errorMessage);
                        this.twoFactor = false;
                        this.orgId = null;
                        this.userId = null;
                    }
                });
            },
        },
    };
</script>
